import type { AriaAttributes, CSSProperties } from 'react';

import classNames from 'classnames';
import omit from 'lodash/omit';

import styles from './SpeechBubble.module.css';

import type { SpacingProps } from '../../styles';
import { Grid, GridItem } from '../Grid';
import { Text } from '../Text';
import { Tail } from './Tail';

export interface SpeechBubbleProps extends SpacingProps, AriaAttributes {
  id?: string;
  color?: 'kale' | 'carrot' | 'white';
  /* Render a custom element. */
  as?: React.ElementType;
  className?: string;
  /* Render a styled header. */
  heading?: string;
  style?: CSSProperties;
  /** @defaultValue 'start' */
  tailPosition?: 'start' | 'end';
  /**
   * Content of the speech bubble. We use a ReactElement for more options when
   * formatting.
   */
  children: React.ReactElement | string;
}

/**
 * Render text in a speech bubble with a tail on the left or right side.
 *
 * @see https://corgi-x.tfd.engineering/components/speechbubble
 */
export const SpeechBubble = ({
  id,
  as,
  color = 'kale',
  heading,
  children,
  tailPosition = 'start',
  ...props
}: SpeechBubbleProps) => {
  const Component = as ?? 'div';

  const fontColor =
    color === 'white' ? 'Carrot2' : color === 'carrot' ? 'Carrot3' : 'Kale2';

  const className = classNames(
    styles[color],
    props?.className,
    styles.speechBubble
  );

  const ariaAttributes: AriaAttributes = omit(props, [
    'as',
    'className',
    'variant',
    'heading',
    'color',
    'tailPosition',
    'children',
  ]);

  return (
    <Component className={className} {...ariaAttributes}>
      <Grid>
        {heading && (
          <GridItem>
            <Text bold color={fontColor} topSpacing="none" bottomSpacing="xs">
              {heading}
            </Text>
          </GridItem>
        )}
        <GridItem>
          <Text as="p" vSpacing="none" variant="article-16" color={fontColor}>
            {children}
          </Text>
        </GridItem>
        <Tail position={tailPosition} color={color} />
      </Grid>
    </Component>
  );
};
